import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const navHeight = 80; // Fixed navigation height
      const targetPosition = element.getBoundingClientRect().top + window.pageYOffset - navHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
      
      setIsMobileMenuOpen(false);
    }
  };

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'The Pod' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'amenities', label: 'Amenities' },
    { id: 'location', label: 'Location' },
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-sm' 
          : 'bg-transparent'
      }`}>
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-5">
          <div className="flex items-center justify-between">
            {/* Logo - Hidden when mobile menu is open */}
            <button 
              onClick={() => scrollToSection('home')}
              className={`text-base sm:text-lg font-medium transition-all duration-300 ${
                isMobileMenuOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
              } ${
                isScrolled ? 'text-[#071936]' : 'text-white'
              }`}
            >
              The Tullaghan Pod
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.slice(1).map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className={`text-sm font-normal transition-colors duration-300 ${
                    isScrolled ? 'text-[#1F2937] hover:text-[#2AA7A1]' : 'text-white/90 hover:text-white'
                  }`}
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => scrollToSection('booking')}
                className={`px-6 py-2.5 text-sm font-medium transition-all duration-300 ${
                  isScrolled 
                    ? 'bg-[#2AA7A1] text-white hover:bg-[#238f8a]' 
                    : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
                }`}
              >
                Reserve
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden p-2 transition-colors z-50 ${
                isMobileMenuOpen ? 'text-white' : (isScrolled ? 'text-[#071936]' : 'text-white')
              }`}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Full Screen Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 z-40 md:hidden transition-all duration-500 ${
          isMobileMenuOpen 
            ? 'opacity-100 translate-x-0' 
            : 'opacity-0 -translate-x-full pointer-events-none'
        }`}
        style={{ backgroundColor: '#0f2a30' }}
      >
        <div className="h-full flex flex-col justify-center px-8">
          {/* Menu Header - Smooth fade in */}
          <div className={`absolute top-6 left-6 transition-all duration-700 delay-100 ${
            isMobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
          }`}>
            <h2 className="text-white text-lg font-medium tracking-wide">The Tullaghan Pod</h2>
          </div>

          {/* Menu Items - Staggered animation */}
          <nav className="space-y-7">
            {navLinks.map((link, index) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`block w-full text-left text-white text-2xl font-light hover:text-[#2AA7A1] transition-all duration-300 py-2 ${
                  isMobileMenuOpen 
                    ? 'opacity-100 translate-x-0' 
                    : 'opacity-0 -translate-x-8'
                }`}
                style={{ 
                  transitionDelay: isMobileMenuOpen ? `${(index + 1) * 80}ms` : '0ms' 
                }}
              >
                {link.label}
              </button>
            ))}
            
            {/* Reserve Button in Menu */}
            <button
              onClick={() => scrollToSection('booking')}
              className={`block w-full text-left text-white text-2xl font-light hover:text-[#2AA7A1] transition-all duration-300 py-2 mt-4 border-t border-white/20 pt-8 ${
                isMobileMenuOpen 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 -translate-x-8'
              }`}
              style={{ 
                transitionDelay: isMobileMenuOpen ? `${(navLinks.length + 1) * 80}ms` : '0ms' 
              }}
            >
              Reserve
            </button>
          </nav>
        </div>
      </div>
    </>
  );
};
